/**
 * Valid sample answers for the Singapore private eligibility assessment flow.
 * Dev-only: multiple personas so "Fill random demo" trains feedback with varied inputs.
 */

const SHARED_DECLARATIONS = {
  no_outstanding_issues: true,
  meets_local_shareholding: true,
  authorized_to_apply: true,
  cpf_compliance_ok: true,
  tax_compliance_ok: true,
  regulatory_compliance_ok: true,
  prior_grant_breach: false,
  project_not_commenced: true,
  no_debarment_or_insolvency: true,
};

const SHARED_TAIL = {
  grantType: 'psg',
  latest_fy_end_date: '31/12/2024',
  is_group_company: 'no',
  group_relationship: '',
  parent_company_name: '',
  previous_grants: [],
  previous_grants_other: false,
  previous_grants_none: true,
  prior_apps: ['None previously received'],
  target_market: '',
  expansion_activity_types: [],
  existing_target_market_presence: '',
  assessmentMode: 'comprehensive',
  includeAlternatives: true,
  priorityGrants: [],
  entity_type: 'Private Limited',
};

const PROFILE_A = {
  ...SHARED_TAIL,
  companyName: 'Nexus Automation Pte Ltd',
  uen: '201812345K',
  incorporation_year: 2018,
  industry: 'Manufacturing & Engineering',
  company_email: 'finance@nexus-automation.demo',
  contact_phone: '+65 6122 1001',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '112',
  annual_revenue_sgd: '12800000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Automation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '185000',
  project_objectives: ['Increase productivity', 'Reduce costs'],
  expected_outcomes:
    'Automate two production lines and cut manual handling errors by roughly 30% within nine months, with line-side dashboards for supervisors.',
  cofunding_capacity: 55,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

const PROFILE_B = {
  ...SHARED_TAIL,
  companyName: 'Harbourlight F&B Holdings Pte Ltd',
  uen: '201534567M',
  incorporation_year: 2015,
  industry: 'Food & Beverage',
  company_email: 'ops@harbourlight-fb.demo',
  contact_phone: '+65 6234 8890',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '3–6 months',
  employees: '48',
  annual_revenue_sgd: '3200000',
  local_ownership_pct: '51',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Digital Transformation',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '3-6 months',
  project_budget_sgd: '72000',
  project_objectives: ['Digital adoption', 'Improve quality'],
  expected_outcomes:
    'Roll out integrated POS, kitchen display, and inventory sync across four outlets to shorten order-to-serve time and reduce stock-outs.',
  cofunding_capacity: 45,
  business_function_impacted: 'Operations',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '2',
  can_fund_upfront: 'Yes',
  application_priority: 'Fastest project start',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '6-12 months',
};

const PROFILE_C = {
  ...SHARED_TAIL,
  companyName: 'Sterling BioAnalytics Pte Ltd',
  uen: '201912345W',
  incorporation_year: 2019,
  industry: 'Healthcare & Life Sciences',
  company_email: 'grants@sterling-bio.demo',
  contact_phone: '+65 6100 2244',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: 'More than 12 months',
  employees: '67',
  annual_revenue_sgd: '8900000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'R&D',
  project_maturity: 'Proof of concept (POC)',
  expected_timeline: '1-2 years',
  project_budget_sgd: '240000',
  project_objectives: ['Enhance capabilities', 'Enter new markets'],
  expected_outcomes:
    'Validate a benchtop assay workflow with two pilot hospital partners and document SOPs for regulatory readiness before wider deployment.',
  cofunding_capacity: 50,
  business_function_impacted: 'Compliance',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '1',
  can_fund_upfront: 'Not sure',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'No',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

const PROFILE_D = {
  ...SHARED_TAIL,
  companyName: 'Urban Studio Collective Pte Ltd',
  uen: '201712890B',
  incorporation_year: 2017,
  industry: 'Creative & Media',
  company_email: 'hello@urbanstudio.demo',
  contact_phone: '+65 6987 3310',
  business_stage: 'Startup',
  financial_viability: true,
  cash_runway_band: 'Less than 3 months',
  employees: '14',
  annual_revenue_sgd: '890000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Skills Development',
  project_maturity: 'Not specified',
  expected_timeline: '6-12 months',
  project_budget_sgd: '45000',
  project_objectives: ['Upskill workforce', 'Enhance capabilities'],
  expected_outcomes:
    'Train the core delivery team on new motion and XR pipelines with certified modules and measurable project throughput gains per sprint.',
  cofunding_capacity: 40,
  business_function_impacted: 'HR',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: 'None received / NIL',
  can_fund_upfront: 'No',
  application_priority: 'Fastest submission',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

const PROFILE_E = {
  ...SHARED_TAIL,
  companyName: 'ApexChain Logistics Pte Ltd',
  uen: '200845678C',
  incorporation_year: 2008,
  industry: 'Logistics & Supply Chain',
  company_email: 'projects@apexchain.demo',
  contact_phone: '+65 6777 4400',
  business_stage: 'Mature',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '238',
  annual_revenue_sgd: '42000000',
  local_ownership_pct: '75',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Innovation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '1-2 years',
  project_budget_sgd: '420000',
  project_objectives: ['Reduce costs', 'Sustainability goals'],
  expected_outcomes:
    'Pilot route-optimisation and warehouse energy telemetry at the Tuas hub; target measurable fuel and kWh savings versus FY baseline.',
  cofunding_capacity: 60,
  business_function_impacted: 'Supply Chain',
  vendor_selected: 'Yes',
  vendor_country: 'Overseas',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '6-12 months',
};

const PROFILE_F = {
  ...SHARED_TAIL,
  companyName: 'GreenStride Retail Pte Ltd',
  uen: '202012345A',
  incorporation_year: 2020,
  industry: 'Retail & E-commerce',
  company_email: 'cx@greenstride.demo',
  contact_phone: '+65 6555 9012',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '3–6 months',
  employees: '33',
  annual_revenue_sgd: '5100000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Market Expansion',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '128000',
  project_objectives: ['Enter new markets', 'Digital adoption'],
  expected_outcomes:
    'Launch omnichannel fulfilment for Malaysia and Vietnam test markets with localised payments and returns, tracked against conversion KPIs.',
  cofunding_capacity: 50,
  business_function_impacted: 'Sales',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '2',
  can_fund_upfront: 'Yes',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

const PROFILE_G = {
  ...SHARED_TAIL,
  companyName: 'Summit Financial Advisory Pte Ltd',
  uen: '201445612H',
  incorporation_year: 2014,
  industry: 'Financial Services',
  company_email: 'compliance@summit-fa.demo',
  contact_phone: '+65 6888 2201',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: 'More than 12 months',
  employees: '92',
  annual_revenue_sgd: '15600000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Digital Transformation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '210000',
  project_objectives: ['Digital adoption', 'Improve quality'],
  expected_outcomes:
    'Replace legacy client onboarding and KYC workflows with a unified platform, reducing manual rework and audit findings on sample reviews.',
  cofunding_capacity: 50,
  business_function_impacted: 'Compliance',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Fastest submission',
  scope_flexibility: 'No',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

const PROFILE_H = {
  ...SHARED_TAIL,
  companyName: 'SolarGrid Construction Pte Ltd',
  uen: '201156789D',
  incorporation_year: 2011,
  industry: 'Construction & Real Estate',
  company_email: 'pmo@solargrid.demo',
  contact_phone: '+65 6333 7788',
  business_stage: 'Mature',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '156',
  annual_revenue_sgd: '22100000',
  local_ownership_pct: '60',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Sustainability',
  project_maturity: 'Proof of concept (POC)',
  expected_timeline: '1-2 years',
  project_budget_sgd: '310000',
  project_objectives: ['Sustainability goals', 'Reduce costs'],
  expected_outcomes:
    'Install rooftop solar and monitoring on two industrial sites with documented generation versus grid draw and payback assumptions for management.',
  cofunding_capacity: 55,
  business_function_impacted: 'Operations',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '2',
  can_fund_upfront: 'Not sure',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '6-12 months',
};

/** Group structure + prior PSG; tests subsidiary / parent fields */
const PROFILE_I = {
  ...SHARED_TAIL,
  companyName: 'LatticeWorks Software Pte Ltd',
  uen: '202134567K',
  incorporation_year: 2021,
  industry: 'Technology & Software',
  company_email: 'ops@latticeworks.demo',
  contact_phone: '+65 6012 8844',
  is_group_company: 'yes',
  group_relationship: 'Subsidiary',
  parent_company_name: 'LatticeWorks Group Holdings Ltd',
  previous_grants: ['PSG'],
  previous_grants_other: false,
  previous_grants_none: false,
  prior_apps: ['PSG'],
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '86',
  annual_revenue_sgd: '14200000',
  local_ownership_pct: '100',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Singapore tax resident',
    'Singapore employees',
    'Regional headquarters in Singapore',
  ],
  project_type: 'Digital Transformation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '198000',
  project_objectives: ['Digital adoption', 'Improve quality'],
  expected_outcomes:
    'Consolidate CRM, billing, and service desk on one cloud stack; cut order-to-cash manual steps and improve repeat-customer visibility for the Singapore entity.',
  cofunding_capacity: 52,
  business_function_impacted: 'IT',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Fastest project start',
  scope_flexibility: 'Yes',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Project proposal/business plan',
    'Vendor proposals/contracts',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

/** Related-party vendor + overseas supplier; tests diligence paths */
const PROFILE_J = {
  ...SHARED_TAIL,
  companyName: 'NorthPoint Advisory Partners Pte Ltd',
  uen: '201667890E',
  incorporation_year: 2016,
  industry: 'Professional Services',
  company_email: 'delivery@northpoint-advisory.demo',
  contact_phone: '+65 6221 5509',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: 'More than 12 months',
  employees: '54',
  annual_revenue_sgd: '11800000',
  local_ownership_pct: '100',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Overseas operations with Singapore presence',
    'Singapore bank account',
  ],
  project_type: 'Innovation',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '1-2 years',
  project_budget_sgd: '265000',
  project_objectives: ['Enhance capabilities', 'Digital adoption'],
  expected_outcomes:
    'Build a client-facing analytics layer on top of engagement data; pilot with two anchor accounts before firm-wide rollout and training.',
  cofunding_capacity: 48,
  business_function_impacted: 'Customer Service',
  vendor_selected: 'Yes',
  vendor_country: 'Overseas',
  vendor_related_party: 'Yes',
  quotation_count: '2',
  can_fund_upfront: 'Not sure',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Maybe',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Project quotations/estimates',
    'Company organizational chart',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '6–12 months',
};

/** Lean edtech; lower runway + co-funding stress */
const PROFILE_K = {
  ...SHARED_TAIL,
  companyName: 'BrightPath Learning Labs Pte Ltd',
  uen: '202289901C',
  incorporation_year: 2022,
  industry: 'Education & Training',
  company_email: 'hello@brightpath-learning.demo',
  contact_phone: '+65 6909 7712',
  business_stage: 'Startup',
  financial_viability: false,
  cash_runway_band: 'Less than 3 months',
  employees: '22',
  annual_revenue_sgd: '1800000',
  local_ownership_pct: '70',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore employees'],
  project_type: 'Skills Development',
  project_maturity: 'Proof of concept (POC)',
  expected_timeline: '3-6 months',
  project_budget_sgd: '68000',
  project_objectives: ['Upskill workforce', 'Enhance capabilities'],
  expected_outcomes:
    'Roll out an LMS with skills badges for facilitators; track completion and learner NPS before expanding to corporate clients.',
  cofunding_capacity: 35,
  business_function_impacted: 'HR',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '1',
  can_fund_upfront: 'No',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'No',
  available_documents: ['ACRA Business Profile', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '12–24 months',
};

/** In-house build; quotations N.A. */
const PROFILE_L = {
  ...SHARED_TAIL,
  companyName: 'VoltaGrid Energy Pte Ltd',
  uen: '201998812N',
  incorporation_year: 2019,
  industry: 'Energy & Sustainability',
  company_email: 'innovation@voltagrid.demo',
  contact_phone: '+65 6799 3300',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '3–6 months',
  employees: '41',
  annual_revenue_sgd: '6700000',
  local_ownership_pct: '85',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Singapore office/warehouse',
  ],
  project_type: 'Other',
  project_maturity: 'Proof of concept (POC)',
  expected_timeline: '2+ years',
  project_budget_sgd: '335000',
  project_objectives: ['Sustainability goals', 'Reduce costs'],
  expected_outcomes:
    'Design and pilot a battery-aware load management controller for one facility; measure peak demand reduction vs baseline before scaling.',
  cofunding_capacity: 58,
  business_function_impacted: 'Operations',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: 'Not applicable',
  can_fund_upfront: 'Yes',
  application_priority: 'Not sure',
  scope_flexibility: 'Maybe',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Project proposal/business plan',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

/** Regional HQ; export-led expansion + grant history */
const PROFILE_M = {
  ...SHARED_TAIL,
  companyName: 'OrchidTrade Maritime Pte Ltd',
  uen: '200823456B',
  incorporation_year: 2008,
  industry: 'Logistics & Supply Chain',
  company_email: 'strategy@orchidtrade.demo',
  contact_phone: '+65 6510 9900',
  previous_grants: ['EDG', 'MRA'],
  previous_grants_other: false,
  previous_grants_none: false,
  prior_apps: ['EDG', 'MRA'],
  business_stage: 'Mature',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '312',
  annual_revenue_sgd: '58000000',
  local_ownership_pct: '55',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Regional headquarters in Singapore',
    'Overseas operations with Singapore presence',
    'Singapore bank account',
  ],
  project_type: 'Market Expansion',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '1-2 years',
  project_budget_sgd: '495000',
  project_objectives: ['Enter new markets', 'Improve quality'],
  expected_outcomes:
    'Stand up ASEAN trade documentation hub (TH/VN/ID lanes), with compliance playbooks and local partner onboarding in two pilot corridors.',
  cofunding_capacity: 62,
  business_function_impacted: 'Supply Chain',
  vendor_selected: 'Yes',
  vendor_country: 'Multiple / Not sure',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Fastest submission',
  scope_flexibility: 'Yes',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Vendor proposals/contracts',
    'Tax clearance certificate',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: '6–12 months',
};

/** IT-led automation; local shareholding edge case */
const PROFILE_N = {
  ...SHARED_TAIL,
  companyName: 'Cipherlane Cybersecurity Pte Ltd',
  uen: '202056789D',
  incorporation_year: 2020,
  industry: 'Technology & Software',
  company_email: 'grants@cipherlane.demo',
  contact_phone: '+65 6444 2108',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '3–6 months',
  employees: '39',
  annual_revenue_sgd: '9600000',
  local_ownership_pct: '30',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Singapore tax resident',
  ],
  project_type: 'Automation',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '142000',
  project_objectives: ['Increase productivity', 'Digital adoption'],
  expected_outcomes:
    'Automate ticket triage and vulnerability reporting for managed SOC clients; shorten mean time to acknowledge critical alerts.',
  cofunding_capacity: 47,
  business_function_impacted: 'IT',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '2',
  can_fund_upfront: 'Not sure',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Maybe',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Vendor proposals/contracts',
    'CPF clearance certificate',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

/** Tuned for expanded funding-engine review: non-government pathways across pilot, accelerator, and financing. */
const PROFILE_O = {
  ...SHARED_TAIL,
  companyName: 'HelioGrid Climate Systems Pte Ltd',
  uen: '202245678R',
  incorporation_year: 2022,
  industry: 'Climate Technology & Energy',
  company_email: 'funding@heliogrid.demo',
  contact_phone: '+65 6338 4412',
  entity_type: 'Private Limited',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '46',
  annual_revenue_sgd: '4200000',
  local_ownership_pct: '100',
  operational_presence: [
    'Singapore-based operations',
    'Singapore-registered company',
    'Singapore tax resident',
    'Pilot deployment base in Singapore',
  ],
  project_type: 'Pilot',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '260000',
  project_objectives: ['Pilot deployment', 'Market validation', 'Regional expansion'],
  expected_outcomes:
    'Deploy grid-efficiency monitoring with one utilities pilot and two industrial sites, then convert pilot results into regional commercial expansion.',
  cofunding_capacity: 55,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: [
    'ACRA Business Profile',
    'Financial statements (last 3 years)',
    'Vendor quotations',
    'Project proposal/business plan',
  ],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'scaleup',
  funding_goal: 'pilot',
  funding_preference: 'blended',
  revenue_status: 'profitable',
  business_model: 'deeptech',
  deployment_environment: 'sector_pilot',
  primary_use_of_funds: 'pilot_delivery',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'active_pilots',
  market_scope: 'singapore_plus_asean',
  application_readiness: 'ready_to_apply',
  vc_backed: true,
  pilot_ready: true,
  impact_intent: true,
};

/** Non-government demo: impact-first social enterprise profile */
const PROFILE_P = {
  ...SHARED_TAIL,
  companyName: 'KindLoop Circular Ventures Pte Ltd',
  uen: '202178901M',
  incorporation_year: 2021,
  industry: 'Sustainability & Circular Economy',
  company_email: 'founder@kindloop.demo',
  contact_phone: '+65 6901 2288',
  entity_type: 'Private Limited',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '3–6 months',
  employees: '28',
  annual_revenue_sgd: '1900000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore tax resident'],
  project_type: 'Sustainability',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '145000',
  project_objectives: ['Sustainability goals', 'Market validation', 'Enhance capabilities'],
  expected_outcomes:
    'Scale reusable packaging recovery operations across foodservice partners and prove measurable waste-diversion outcomes with documented reporting.',
  cofunding_capacity: 42,
  business_function_impacted: 'Operations',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '2',
  can_fund_upfront: 'Not sure',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'social_enterprise',
  funding_goal: 'impact_scale',
  funding_preference: 'non_dilutive',
  revenue_status: 'early_revenue',
  business_model: 'b2b',
  deployment_environment: 'sector_pilot',
  primary_use_of_funds: 'market_access',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'active_pilots',
  market_scope: 'singapore_only',
  application_readiness: 'needs_refinement',
  vc_backed: false,
  pilot_ready: true,
  impact_intent: true,
};

/** Non-government demo: VC-backed scale-up suited for equity + venture debt */
const PROFILE_Q = {
  ...SHARED_TAIL,
  companyName: 'AxisCompute AI Pte Ltd',
  uen: '201945612G',
  incorporation_year: 2019,
  industry: 'Technology & Software',
  company_email: 'capital@axiscompute.demo',
  contact_phone: '+65 6230 7719',
  entity_type: 'Private Limited',
  business_stage: 'Scale-up',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '94',
  annual_revenue_sgd: '12800000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore office/warehouse'],
  project_type: 'Innovation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '380000',
  project_objectives: ['Enhance capabilities', 'Regional expansion', 'Market validation'],
  expected_outcomes:
    'Expand model-serving infrastructure for enterprise deployments and shorten onboarding timelines for regional contracts through standardised implementation playbooks.',
  cofunding_capacity: 62,
  business_function_impacted: 'IT',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'scaleup',
  funding_goal: 'market_validation',
  funding_preference: 'debt',
  revenue_status: 'profitable',
  business_model: 'saas',
  deployment_environment: 'live_commercial',
  primary_use_of_funds: 'working_capital',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'recurring_revenue',
  market_scope: 'singapore_plus_asean',
  application_readiness: 'ready_to_apply',
  vc_backed: true,
  pilot_ready: false,
  impact_intent: false,
};

/** Non-government demo: pre-revenue founder profile for accelerator pathways */
const PROFILE_R = {
  ...SHARED_TAIL,
  companyName: 'PulseForge Robotics Pte Ltd',
  uen: '202367890W',
  incorporation_year: 2023,
  industry: 'Manufacturing & Engineering',
  company_email: 'team@pulseforge.demo',
  contact_phone: '+65 6011 5528',
  entity_type: 'Private Limited',
  business_stage: 'Startup',
  financial_viability: false,
  cash_runway_band: 'Less than 3 months',
  employees: '12',
  annual_revenue_sgd: '0',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'R&D',
  project_maturity: 'Proof of concept (POC)',
  expected_timeline: '1-2 years',
  project_budget_sgd: '210000',
  project_objectives: ['Enhance capabilities', 'Pilot deployment'],
  expected_outcomes:
    'Build and test an autonomous inspection unit in one logistics facility, with baseline-versus-pilot performance evidence for follow-on commercial validation.',
  cofunding_capacity: 30,
  business_function_impacted: 'Operations',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: 'None received / NIL',
  can_fund_upfront: 'No',
  application_priority: 'Fastest submission',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'startup',
  funding_goal: 'pilot',
  funding_preference: 'equity',
  revenue_status: 'pre_revenue',
  business_model: 'deeptech',
  deployment_environment: 'sector_pilot',
  primary_use_of_funds: 'pilot_delivery',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'lois_or_partners',
  market_scope: 'singapore_only',
  application_readiness: 'planning_stage',
  vc_backed: false,
  pilot_ready: true,
  impact_intent: false,
};

/** Non-government demo: profitable SME suited for financing routes */
const PROFILE_S = {
  ...SHARED_TAIL,
  companyName: 'Meridian FoodTech Services Pte Ltd',
  uen: '201345678A',
  incorporation_year: 2013,
  industry: 'Food & Beverage',
  company_email: 'finance@meridian-foodtech.demo',
  contact_phone: '+65 6770 9912',
  entity_type: 'Private Limited',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '74',
  annual_revenue_sgd: '9600000',
  local_ownership_pct: '80',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore bank account'],
  project_type: 'Digital Transformation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '240000',
  project_objectives: ['Increase productivity', 'Reduce costs', 'Improve quality'],
  expected_outcomes:
    'Roll out multi-site kitchen and fulfillment optimisation with measurable waste, labour-hour, and order-throughput improvements across three facilities.',
  cofunding_capacity: 58,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'sme',
  funding_goal: 'working_capital',
  funding_preference: 'debt',
  revenue_status: 'profitable',
  business_model: 'b2b',
  deployment_environment: 'internal_operations',
  primary_use_of_funds: 'working_capital',
  target_customer_type: 'sme_clients',
  traction_stage: 'paying_customers',
  market_scope: 'singapore_plus_asean',
  application_readiness: 'ready_to_apply',
  vc_backed: false,
  pilot_ready: false,
  impact_intent: false,
};

/** Non-government demo: climate infra venture suited for challenge + pilot pathways */
const PROFILE_T = {
  ...SHARED_TAIL,
  companyName: 'TerraMesh Infrastructure Analytics Pte Ltd',
  uen: '202156780N',
  incorporation_year: 2021,
  industry: 'Built Environment & Infrastructure',
  company_email: 'grants@terramesh.demo',
  contact_phone: '+65 6712 3344',
  entity_type: 'Private Limited',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '39',
  annual_revenue_sgd: '3600000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Pilot deployment base in Singapore'],
  project_type: 'Pilot',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '315000',
  project_objectives: ['Pilot deployment', 'Market validation', 'Enhance capabilities'],
  expected_outcomes:
    'Deploy structural-health monitoring pilots with two facility operators and convert validated outcomes into recurring enterprise contracts.',
  cofunding_capacity: 48,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'sme',
  funding_goal: 'pilot',
  funding_preference: 'non_dilutive',
  revenue_status: 'revenue_generating',
  business_model: 'b2b',
  deployment_environment: 'sector_pilot',
  primary_use_of_funds: 'pilot_delivery',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'active_pilots',
  market_scope: 'singapore_only',
  application_readiness: 'ready_to_apply',
  vc_backed: false,
  pilot_ready: true,
  impact_intent: true,
};

/** Non-government demo: healthtech SME suited for debt + RBF */
const PROFILE_U = {
  ...SHARED_TAIL,
  companyName: 'MedCircuit Digital Care Pte Ltd',
  uen: '201867421K',
  incorporation_year: 2018,
  industry: 'Healthcare & MedTech',
  company_email: 'finance@medcircuit.demo',
  contact_phone: '+65 6881 2277',
  entity_type: 'Private Limited',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '63',
  annual_revenue_sgd: '7300000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore bank account'],
  project_type: 'Digital Transformation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '285000',
  project_objectives: ['Increase productivity', 'Reduce costs', 'Regional expansion'],
  expected_outcomes:
    'Scale remote-care workflow automation across clinical partners while improving care-cycle efficiency and reducing delivery bottlenecks.',
  cofunding_capacity: 52,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'sme',
  funding_goal: 'working_capital',
  funding_preference: 'debt',
  revenue_status: 'profitable',
  business_model: 'b2b',
  deployment_environment: 'internal_operations',
  primary_use_of_funds: 'working_capital',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'recurring_revenue',
  market_scope: 'singapore_plus_asean',
  application_readiness: 'ready_to_apply',
  vc_backed: false,
  pilot_ready: false,
  impact_intent: false,
};

/** Non-government demo: deeptech scale-up suited for equity + venture debt */
const PROFILE_V = {
  ...SHARED_TAIL,
  companyName: 'QuantumEdge Compute Systems Pte Ltd',
  uen: '202012389R',
  incorporation_year: 2020,
  industry: 'Advanced Technology & DeepTech',
  company_email: 'capital@quantumedge.demo',
  contact_phone: '+65 6332 1190',
  entity_type: 'Private Limited',
  business_stage: 'Scale-up',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '88',
  annual_revenue_sgd: '11100000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company', 'Singapore office/warehouse'],
  project_type: 'Innovation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '6-12 months',
  project_budget_sgd: '420000',
  project_objectives: ['Enhance capabilities', 'Market validation', 'Regional expansion'],
  expected_outcomes:
    'Expand enterprise-grade compute infrastructure and improve deployment throughput to support larger regional contracts.',
  cofunding_capacity: 60,
  business_function_impacted: 'IT',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
  eligible_entity_type: 'scaleup',
  funding_goal: 'market_validation',
  funding_preference: 'blended',
  revenue_status: 'profitable',
  business_model: 'saas',
  deployment_environment: 'live_commercial',
  primary_use_of_funds: 'capability_build',
  target_customer_type: 'enterprise_clients',
  traction_stage: 'recurring_revenue',
  market_scope: 'singapore_plus_asean',
  application_readiness: 'ready_to_apply',
  vc_backed: true,
  pilot_ready: false,
  impact_intent: false,
};

/** Additional gov-focused demo: services SME with clear PSG/EDG-style profile */
const PROFILE_W = {
  ...SHARED_TAIL,
  companyName: 'BlueHarbor Workflow Systems Pte Ltd',
  uen: '201745321H',
  incorporation_year: 2017,
  industry: 'Professional Services',
  company_email: 'admin@blueharbor.demo',
  contact_phone: '+65 6345 7721',
  business_stage: 'Established',
  financial_viability: true,
  cash_runway_band: '6–12 months',
  employees: '36',
  annual_revenue_sgd: '4100000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Digital Transformation',
  project_maturity: 'Implementation / scale-up',
  expected_timeline: '3-6 months',
  project_budget_sgd: '98000',
  project_objectives: ['Increase productivity', 'Improve quality', 'Reduce costs'],
  expected_outcomes:
    'Implement a unified operations stack for project delivery, invoicing, and QA checks to reduce rework and shorten cycle time.',
  cofunding_capacity: 47,
  business_function_impacted: 'Operations',
  vendor_selected: 'Yes',
  vendor_country: 'Singapore',
  vendor_related_party: 'No',
  quotation_count: '3 or more',
  can_fund_upfront: 'Yes',
  application_priority: 'Maximum funding amount',
  scope_flexibility: 'Yes',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Vendor quotations'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

/** Additional gov-focused demo: export-oriented SME with MRA-style profile */
const PROFILE_X = {
  ...SHARED_TAIL,
  companyName: 'Orchid Motion Components Pte Ltd',
  uen: '201623457P',
  incorporation_year: 2016,
  industry: 'Precision Engineering',
  company_email: 'growth@orchidmotion.demo',
  contact_phone: '+65 6899 1184',
  business_stage: 'Growth',
  financial_viability: true,
  cash_runway_band: 'More than 12 months',
  employees: '74',
  annual_revenue_sgd: '9600000',
  local_ownership_pct: '100',
  operational_presence: ['Singapore-based operations', 'Singapore-registered company'],
  project_type: 'Market Expansion',
  project_maturity: 'Proof of value (POV)',
  expected_timeline: '6-12 months',
  project_budget_sgd: '158000',
  project_objectives: ['Enter new markets', 'Market validation', 'Enhance capabilities'],
  expected_outcomes:
    'Run structured overseas market-entry activities for two ASEAN target markets with partner validation and a conversion-focused go-to-market plan.',
  cofunding_capacity: 53,
  business_function_impacted: 'Business Development',
  vendor_selected: 'No',
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '2',
  can_fund_upfront: 'Yes',
  application_priority: 'Highest approval chance',
  scope_flexibility: 'Maybe',
  available_documents: ['ACRA Business Profile', 'Financial statements (last 3 years)', 'Project proposal/business plan'],
  declarations: { ...SHARED_DECLARATIONS },
  financials_recency: 'Within 6 months',
};

export const SINGAPORE_PRIVATE_DUMMY_PROFILES = [
  PROFILE_A,
  PROFILE_B,
  PROFILE_C,
  PROFILE_D,
  PROFILE_E,
  PROFILE_F,
  PROFILE_G,
  PROFILE_H,
  PROFILE_I,
  PROFILE_J,
  PROFILE_K,
  PROFILE_L,
  PROFILE_M,
  PROFILE_N,
  PROFILE_W,
  PROFILE_X,
];

export const SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY = PROFILE_O;
export const SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES = [
  PROFILE_O,
  PROFILE_P,
  PROFILE_Q,
  PROFILE_R,
  PROFILE_S,
  PROFILE_T,
  PROFILE_U,
  PROFILE_V,
];

/** @deprecated Use getRandomSingaporePrivateDummyFormData() for dev fills */
export const SINGAPORE_PRIVATE_ELIGIBILITY_DUMMY = SINGAPORE_PRIVATE_DUMMY_PROFILES[0];

/**
 * Returns a deep copy of a randomly chosen valid demo profile (new object each call).
 */
export function getRandomSingaporePrivateDummyFormData() {
  const list = SINGAPORE_PRIVATE_DUMMY_PROFILES;
  const idx = Math.floor(Math.random() * list.length);
  const raw = list[idx];
  return {
    ...raw,
    declarations: { ...raw.declarations },
  };
}

export function getSingaporePrivateNonGovernmentDummyFormData() {
  return {
    ...SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY,
    declarations: { ...SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY.declarations },
  };
}

export function getRandomSingaporePrivateNonGovernmentDummyFormData() {
  const list = SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES;
  const idx = Math.floor(Math.random() * list.length);
  const raw = list[idx];
  return {
    ...raw,
    declarations: { ...raw.declarations },
  };
}

function cloneSingaporeGrantShowcaseProfile(base, overrides) {
  return {
    ...base,
    ...overrides,
    declarations: {
      ...(base.declarations || {}),
      ...((overrides && overrides.declarations) || {}),
    },
  };
}

const SINGAPORE_GRANT_SHOWCASE_PROFILES = {
  ai_compute: cloneSingaporeGrantShowcaseProfile(PROFILE_V, {
    companyName: "AxisCompute AI Pte Ltd",
    industry: "Technology & Software",
    project_type: "AI Automation MVP",
    project_description:
      "Build and pilot an AI copilot for customer support and internal workflow automation using cloud compute and analytics dashboards.",
    project_maturity: "Proof of value (POV)",
    expected_timeline: "3-6 months",
    project_budget_sgd: "168000",
    project_objectives: ["Increase productivity", "Digital adoption", "Enhance capabilities"],
    expected_outcomes:
      "Deploy an AI assistant for customer service and internal workflow automation, with MVP milestones, analytics dashboards, and measurable productivity gains.",
    cofunding_capacity: 55,
    business_function_impacted: "IT",
    deployment_environment: "internal_operations",
    primary_use_of_funds: "software_tools",
    target_customer_type: "enterprise_clients",
    traction_stage: "active_pilots",
    market_scope: "singapore_only",
    application_readiness: "ready_to_apply",
    eligible_entity_type: "sme",
    funding_goal: "digital_transformation",
    funding_preference: "non_dilutive",
    revenue_status: "revenue_generating",
    business_model: "b2b",
    vc_backed: false,
    pilot_ready: true,
    impact_intent: false,
  }),
  green_financing: cloneSingaporeGrantShowcaseProfile(PROFILE_E, {
    companyName: "HelioGrid Green Systems Pte Ltd",
    industry: "Energy & Sustainability",
    project_type: "Sustainability",
    project_description:
      "Install energy telemetry, carbon accounting dashboards, and efficiency upgrades across facilities, supported by green financing for capex and implementation.",
    project_maturity: "Implementation / scale-up",
    expected_timeline: "6-12 months",
    project_budget_sgd: "335000",
    project_objectives: ["Sustainability goals", "Reduce costs", "Improve quality"],
    expected_outcomes:
      "Cut energy use and emissions with monitored savings, decarbonisation reporting, and a financeable capex plan for phased rollout.",
    cofunding_capacity: 52,
    business_function_impacted: "Operations",
    deployment_environment: "sector_pilot",
    primary_use_of_funds: "capex",
    target_customer_type: "enterprise_clients",
    traction_stage: "active_pilots",
    market_scope: "singapore_only",
    application_readiness: "ready_to_apply",
    eligible_entity_type: "sme",
    funding_goal: "working_capital",
    funding_preference: "debt",
    revenue_status: "profitable",
    business_model: "b2b",
    can_fund_upfront: "No",
    vc_backed: false,
    pilot_ready: true,
    impact_intent: true,
  }),
  tourism_showcase: cloneSingaporeGrantShowcaseProfile(PROFILE_B, {
    companyName: "Marina Vista Tourism Group Pte Ltd",
    industry: "Hospitality & Tourism",
    project_type: "Tourism Product Development",
    project_description:
      "Refresh a boutique hotel experience and launch cruise-linked visitor packages and business event add-ons that improve guest experience and visitor spend.",
    project_maturity: "Proof of value (POV)",
    expected_timeline: "6-12 months",
    project_budget_sgd: "210000",
    project_objectives: ["Improve quality", "Enter new markets", "Enhance capabilities"],
    expected_outcomes:
      "Rejuvenate hotel rooms, create cruise-linked visitor products, and support MICE and tourism demand with measurable spend uplift.",
    cofunding_capacity: 48,
    business_function_impacted: "Operations",
    deployment_environment: "public_facing",
    primary_use_of_funds: "market_access",
    target_customer_type: "consumer_and_enterprise",
    traction_stage: "paying_customers",
    market_scope: "singapore_only",
    application_readiness: "ready_to_apply",
    eligible_entity_type: "sme",
    funding_goal: "market_validation",
    funding_preference: "non_dilutive",
    revenue_status: "revenue_generating",
    business_model: "b2b",
    can_fund_upfront: "Yes",
    vc_backed: false,
    pilot_ready: true,
    impact_intent: false,
  }),
  association_germany: cloneSingaporeGrantShowcaseProfile(PROFILE_M, {
    companyName: "Singapore-Germany Industry Collaboration Association",
    entity_type: "Association",
    industry: "Trade Association & Industry Collective",
    project_type: "Industry Collaboration",
    project_description:
      "Run a TAC-led joint innovation programme with a German SME partner to support member companies, proof-of-concept pilots, and commercialisation pathways.",
    project_maturity: "Proof of concept (POC)",
    expected_timeline: "1-2 years",
    project_budget_sgd: "265000",
    project_objectives: ["Enhance capabilities", "Market validation", "Enter new markets"],
    expected_outcomes:
      "Build a cross-border innovation roadmap, match Singapore firms with German partners, and run a structured collaboration programme with measurable member outcomes.",
    cofunding_capacity: 50,
    business_function_impacted: "Business Development",
    deployment_environment: "partnership_programme",
    primary_use_of_funds: "partnership_build",
    target_customer_type: "enterprise_clients",
    traction_stage: "active_pilots",
    market_scope: "singapore_plus_asean",
    application_readiness: "ready_to_apply",
    eligible_entity_type: "association",
    funding_goal: "market_validation",
    funding_preference: "blended",
    revenue_status: "revenue_generating",
    business_model: "b2b",
    can_fund_upfront: "Yes",
    vc_backed: false,
    pilot_ready: true,
    impact_intent: true,
  }),
  psg_procurement_ready: cloneSingaporeGrantShowcaseProfile(PROFILE_W, {
    companyName: "LedgerFlow Operations Pte Ltd",
    industry: "Professional Services",
    project_type: "Digital Transformation",
    project_description:
      "Adopt a pre-approved CRM, document workflow, and e-invoicing platform for the Singapore operations team before any vendor payment is made.",
    project_maturity: "Implementation / scale-up",
    expected_timeline: "3-6 months",
    project_budget_sgd: "64000",
    project_objectives: ["Increase productivity", "Digital adoption", "Reduce costs"],
    expected_outcomes:
      "Reduce manual admin effort across client onboarding, invoicing, and monthly reporting, with tracked processing time and rework KPIs.",
    cofunding_capacity: 50,
    business_function_impacted: "Operations",
    vendor_selected: "Yes",
    vendor_country: "Singapore",
    vendor_related_party: "No",
    quotation_count: "3 or more",
    can_fund_upfront: "Yes",
    available_documents: ["ACRA Business Profile", "Financial statements (last 3 years)", "Vendor quotations"],
    application_readiness: "ready_to_apply",
    eligible_entity_type: "sme",
    funding_goal: "digital_transformation",
    funding_preference: "non_dilutive",
    primary_use_of_funds: "software_tools",
  }),
  edg_transformation: cloneSingaporeGrantShowcaseProfile(PROFILE_A, {
    companyName: "Tuas Precision Automation Pte Ltd",
    industry: "Manufacturing & Engineering",
    project_type: "Automation",
    project_description:
      "Redesign production scheduling and deploy machine data capture, automated work orders, and supervisor dashboards across two production lines.",
    project_maturity: "Implementation / scale-up",
    expected_timeline: "6-12 months",
    project_budget_sgd: "285000",
    project_objectives: ["Increase productivity", "Reduce costs", "Improve quality"],
    expected_outcomes:
      "Cut changeover delays, reduce manual production reporting, and improve output visibility with before-and-after productivity measurements.",
    cofunding_capacity: 58,
    business_function_impacted: "Operations",
    vendor_selected: "Yes",
    vendor_country: "Singapore",
    quotation_count: "3 or more",
    application_priority: "Highest approval chance",
    eligible_entity_type: "sme",
    funding_goal: "digital_transformation",
    primary_use_of_funds: "capability_build",
  }),
  mra_export_market: cloneSingaporeGrantShowcaseProfile(PROFILE_X, {
    companyName: "Orchid Motion Components Pte Ltd",
    industry: "Precision Engineering",
    project_type: "Market Expansion",
    project_description:
      "Prepare and execute market-entry activities for Indonesia and Vietnam, including overseas partner search, product localisation, and trade fair participation.",
    project_maturity: "Proof of value (POV)",
    expected_timeline: "6-12 months",
    project_budget_sgd: "118000",
    project_objectives: ["Enter new markets", "Market validation", "Regional expansion"],
    expected_outcomes:
      "Validate two ASEAN distributor channels, build an export pipeline, and secure qualified overseas leads for follow-up commercial conversion.",
    target_market: "Indonesia and Vietnam",
    expansion_activity_types: ["Overseas market promotion", "Business matching", "Market set-up"],
    existing_target_market_presence: "No local entity yet; early distributor conversations underway",
    business_function_impacted: "Business Development",
    market_scope: "singapore_plus_asean",
    primary_use_of_funds: "market_access",
  }),
  eeg_energy_upgrade: cloneSingaporeGrantShowcaseProfile(PROFILE_H, {
    companyName: "CoolChain Fresh Logistics Pte Ltd",
    industry: "Logistics & Supply Chain",
    project_type: "Sustainability",
    project_description:
      "Replace older refrigeration equipment with energy-efficient units and install metering to measure electricity savings across a cold-chain facility.",
    project_maturity: "Implementation / scale-up",
    expected_timeline: "3-6 months",
    project_budget_sgd: "132000",
    project_objectives: ["Sustainability goals", "Reduce costs", "Improve quality"],
    expected_outcomes:
      "Reduce kWh consumption by at least 18%, document carbon savings, and improve temperature stability for perishable goods handling.",
    cofunding_capacity: 54,
    business_function_impacted: "Operations",
    vendor_selected: "Yes",
    vendor_country: "Singapore",
    quotation_count: "2",
    primary_use_of_funds: "capex",
    impact_intent: true,
  }),
  sfec_training: cloneSingaporeGrantShowcaseProfile(PROFILE_D, {
    companyName: "StudioNorth Experience Design Pte Ltd",
    industry: "Creative & Media",
    project_type: "Skills Development",
    project_description:
      "Train designers, producers, and account leads on AI-assisted creative workflows, project governance, and new delivery SOPs.",
    project_maturity: "Implementation / scale-up",
    expected_timeline: "3-6 months",
    project_budget_sgd: "52000",
    project_objectives: ["Upskill workforce", "Enhance capabilities", "Improve quality"],
    expected_outcomes:
      "Lift delivery team capability, reduce revision cycles, and document role-based competency improvements after structured training.",
    business_function_impacted: "HR",
    vendor_selected: "No",
    quotation_count: "2",
    can_fund_upfront: "Yes",
    available_documents: ["ACRA Business Profile", "Financial statements (last 3 years)", "Project proposal/business plan"],
    funding_goal: "capability_build",
    primary_use_of_funds: "training",
  }),
  eci_ai_mvp: cloneSingaporeGrantShowcaseProfile(PROFILE_G, {
    companyName: "SummitAI Advisory Systems Pte Ltd",
    industry: "Financial Services",
    project_type: "AI MVP automation",
    project_description:
      "Build an AI MVP that screens client documents, routes exceptions, and connects CRM and compliance data through secure APIs.",
    project_maturity: "Proof of value (POV)",
    expected_timeline: "3-6 months",
    project_budget_sgd: "176000",
    project_objectives: ["Digital adoption", "Enhance capabilities", "Improve quality"],
    expected_outcomes:
      "Pilot AI-assisted onboarding for two adviser teams, reduce manual review time, and capture accuracy and exception-handling KPIs.",
    business_function_impacted: "Compliance",
    deployment_environment: "internal_operations",
    primary_use_of_funds: "software_tools",
    traction_stage: "active_pilots",
    pilot_ready: true,
  }),
  thin_evidence_government: cloneSingaporeGrantShowcaseProfile(PROFILE_K, {
    companyName: "LeanOps Services Pte Ltd",
    industry: "Business Services",
    project_type: "Business improvement",
    project_description: "Improve internal operations with a better system and process.",
    project_maturity: "Not specified",
    expected_timeline: "Not sure",
    project_budget_sgd: "38000",
    project_objectives: ["Increase productivity"],
    expected_outcomes: "Improve productivity, but KPIs and project scope still need refinement.",
    cofunding_capacity: 30,
    business_function_impacted: "Operations",
    vendor_selected: "No",
    quotation_count: "None received / NIL",
    can_fund_upfront: "No",
    available_documents: ["ACRA Business Profile"],
    financials_recency: "12–24 months",
    application_readiness: "planning_stage",
  }),
  shareholding_edge_case: cloneSingaporeGrantShowcaseProfile(PROFILE_N, {
    companyName: "Cipherlane Cybersecurity Pte Ltd",
    industry: "Technology & Software",
    local_ownership_pct: "25",
    declarations: { meets_local_shareholding: false },
    project_type: "Automation",
    project_description:
      "Automate security ticket triage and vulnerability reporting for managed SOC clients, but local shareholding is below common grant thresholds.",
    project_maturity: "Proof of value (POV)",
    project_budget_sgd: "142000",
    project_objectives: ["Increase productivity", "Digital adoption"],
    expected_outcomes:
      "Shorten alert handling time and improve reporting consistency while highlighting a mandatory eligibility risk for PSG, EDG, MRA, and EEG-style grants.",
    application_readiness: "needs_refinement",
  }),
};

export const SINGAPORE_GRANT_SHOWCASE_PRESETS = [
  {
    key: "ai_compute",
    label: "AI / Compute showcase",
    description: "Surfaces ECI and ADS with AI, automation, analytics, cloud, and implementation signals.",
  },
  {
    key: "green_financing",
    label: "Green / Financing showcase",
    description: "Surfaces ESP and EFS_GREEN with sustainability and green financing signals.",
  },
  {
    key: "tourism_showcase",
    label: "Tourism showcase",
    description: "Surfaces BEIS, HRF, CDF, and TPDF with hotel, cruise, and visitor-demand signals.",
  },
  {
    key: "association_germany",
    label: "Association / Germany showcase",
    description: "Surfaces LEAD and GSFP with TAC-led collaboration and Germany partnership signals.",
  },
  {
    key: "psg_procurement_ready",
    label: "PSG procurement-ready SME",
    description: "Tests pre-approved solution, quotation, and no-prior-payment signals for PSG-style cards.",
  },
  {
    key: "edg_transformation",
    label: "EDG transformation SME",
    description: "Tests automation workplan, milestones, and productivity uplift signals for EDG-style cards.",
  },
  {
    key: "mra_export_market",
    label: "MRA export market SME",
    description: "Tests overseas market, partner-search, trade fair, and market-entry signals.",
  },
  {
    key: "eeg_energy_upgrade",
    label: "EEG energy upgrade SME",
    description: "Tests energy-efficiency equipment, carbon, kWh, and sustainability outcome signals.",
  },
  {
    key: "sfec_training",
    label: "SFEC training SME",
    description: "Tests workforce training, job redesign, and upskilling signals.",
  },
  {
    key: "eci_ai_mvp",
    label: "ECI AI MVP SME",
    description: "Tests AI, MVP, CRM, API, and compute-related project signals.",
  },
  {
    key: "thin_evidence_government",
    label: "Thin-evidence government case",
    description: "Tests low-documentation, vague-scope, and low-confidence card guidance.",
  },
  {
    key: "shareholding_edge_case",
    label: "Shareholding edge case",
    description: "Tests hard eligibility gates where local shareholding is below common thresholds.",
  },
];

const SINGAPORE_RANDOM_GOVERNMENT_SHOWCASE_KEYS = SINGAPORE_GRANT_SHOWCASE_PRESETS.map((preset) => preset.key);

export function getSingaporeGrantShowcaseFormData(key = "ai_compute") {
  const raw = SINGAPORE_GRANT_SHOWCASE_PROFILES[key] || SINGAPORE_GRANT_SHOWCASE_PROFILES.ai_compute;
  return {
    ...raw,
    declarations: { ...raw.declarations },
  };
}

export function getRandomSingaporeGrantShowcaseFormData() {
  const keys = SINGAPORE_RANDOM_GOVERNMENT_SHOWCASE_KEYS;
  const key = keys[Math.floor(Math.random() * keys.length)] || "ai_compute";
  return {
    key,
    data: getSingaporeGrantShowcaseFormData(key),
  };
}

function buildShowcaseGrant(code, overrides = {}) {
  return {
    id: code,
    grantId: code,
    code,
    acronym: code,
    name: overrides.name || code,
    score: overrides.score ?? 60,
    successProbability: overrides.successProbability ?? Math.min(95, Math.max(20, (overrides.score ?? 60) + 4)),
    approval_likelihood: overrides.approval_likelihood ?? Math.min(95, Math.max(20, (overrides.score ?? 60))),
    eligible: overrides.eligible ?? (overrides.score ?? 60) >= 60,
    fit: overrides.fit || {
      label: overrides.score >= 75 ? 'Eligible' : overrides.score >= 60 ? 'Likely' : 'Less Likely',
      color: 'bg-blue-500/20 text-white/90 border-blue-400/40',
    },
    summaryUserBenefits: overrides.summaryUserBenefits || null,
    detailedAnalysis: overrides.detailedAnalysis || {},
    nextSteps: overrides.nextSteps || [],
    referenceData: overrides.referenceData || {},
    whatThisGrantIsFor: overrides.whatThisGrantIsFor || '',
  };
}

const SINGAPORE_GRANT_SHOWCASE_RESULTS = {
  ai_compute: {
    overall_status: 'completed',
    snapshot_id: 'snapshot-showcase-ai-compute',
    generated_at_sgt: '2025-01-01T00:00:00Z',
    inputs_used: getSingaporeGrantShowcaseFormData('ai_compute'),
    grant_assessments: [
      buildShowcaseGrant('ECI', {
        score: 65,
        successProbability: 61,
        approval_likelihood: 59,
        eligible: true,
        fit: { label: 'Likely', color: 'bg-blue-500/20 text-white/90 border-blue-400/40' },
        summaryUserBenefits: {
          headline: 'Possible eligibility for Enterprise Compute Initiative - Internal fit score 42%',
          narrative: 'Project budget of S$168,000 clears the preferred minimum (S$25,000). Project focus: AI Automation MVP. Budget submitted: S$168,000.',
          insights: [
            'Project budget of S$168,000 clears the preferred minimum (S$25,000).',
            'Project focus: AI Automation MVP',
            'Budget submitted: S$168,000',
          ],
        },
        whatThisGrantIsFor:
          'The Enterprise Compute Initiative (ECI) is an Enterprise Singapore programme that helps digitally mature enterprises accelerate AI projects through cloud provider partnerships, access to relevant tools and training, and expert support.',
        referenceData: {
          programmeType: 'programme',
          displayType: 'Programme',
          accessMode: 'application_based',
          cashGrant: false,
          isProgramme: true,
          is_programme: true,
          tags: ['Government', 'Programme', 'AI', 'Automation', 'Productivity', 'MVP / POC / POV', 'Cloud / Compute'],
        },
      }),
      buildShowcaseGrant('ADS', {
        score: 67,
        successProbability: 64,
        approval_likelihood: 62,
        eligible: true,
        fit: { label: 'Validate Fit', color: 'bg-blue-500/20 text-white/90 border-blue-400/40' },
        summaryUserBenefits: {
          headline: 'Good eligibility for Advanced Digital Solutions - Internal fit score 62%',
          narrative: 'Project (AI Automation MVP) aligns strongly with digital transformation focus areas. Project focus: AI Automation MVP. Budget submitted: S$168,000.',
          insights: [
            'Project (AI Automation MVP) aligns strongly with digital transformation focus areas.',
            'Project focus: AI Automation MVP',
            'Budget submitted: S$168,000',
          ],
        },
        whatThisGrantIsFor:
          'Advanced Digital Solutions supports SMEs adopting more advanced or integrated digital solutions that move beyond basic tools and help transform operations.',
        referenceData: {
          programmeType: 'programme',
          displayType: 'Programme',
          accessMode: 'application_based',
          cashGrant: true,
          isProgramme: true,
          is_programme: true,
          tags: ['Government', 'Grant', 'Programme', 'Digital Transformation', 'SME', 'AI & Data'],
        },
      }),
      buildShowcaseGrant('EIS', {
        score: 56,
        successProbability: 54,
        approval_likelihood: 51,
        eligible: true,
        fit: { label: 'Less Likely', color: 'bg-amber-500/20 text-white/90 border-amber-400/40' },
        summaryUserBenefits: {
          headline: 'Possible eligibility for Enterprise Innovation Scheme - Internal fit score 33%',
          narrative: 'Project focus: AI Automation MVP. Budget submitted: S$168,000.',
          insights: ['Project focus: AI Automation MVP', 'Budget submitted: S$168,000'],
        },
        whatThisGrantIsFor:
          'The Enterprise Innovation Scheme (EIS) is a tax incentive for qualifying innovation, R&D, training, and IP activities, with deductions and allowances rather than cash grant reimbursement.',
        referenceData: {
          programmeType: 'tax_scheme',
          displayType: 'Tax Incentive',
          accessMode: 'tax_filing',
          cashGrant: false,
          isProgramme: false,
          is_programme: false,
          tags: ['Government', 'Tax Incentive', 'Innovation', 'R&D', 'Training', 'IP'],
        },
      }),
    ],
  },
};

export function getSingaporeGrantShowcaseAssessmentData(key = "ai_compute") {
  const raw = SINGAPORE_GRANT_SHOWCASE_RESULTS[key] || SINGAPORE_GRANT_SHOWCASE_RESULTS.ai_compute;
  return {
    ...raw,
    inputs_used: {
      ...raw.inputs_used,
      declarations: { ...(raw.inputs_used?.declarations || {}) },
    },
    grant_assessments: raw.grant_assessments.map((grant) => ({
      ...grant,
      summaryUserBenefits: grant.summaryUserBenefits ? { ...grant.summaryUserBenefits, insights: [...(grant.summaryUserBenefits.insights || [])] } : null,
      detailedAnalysis: grant.detailedAnalysis ? { ...grant.detailedAnalysis } : {},
      nextSteps: [...(grant.nextSteps || [])],
      referenceData: { ...(grant.referenceData || {}) },
    })),
  };
}
